import React, { useEffect, useMemo, useState } from "react";
import { Modal, Table, Checkbox, Alert, Spin, message, Tooltip } from "antd";
import { contractorService } from "../../../services/contractor.service";
import { SITE_ACCESS_META } from "@iso/constants/contractorRegisters";

/**
 * Confirmation step for Approve Contractor.
 *
 * Approving a contractor is a COMPLIANCE decision — it has never granted anybody
 * a login, and on its own it still doesn't. That surprised people: a farm would
 * complete a contractor's details, approve them, and then find their workers
 * could not sign in, with nothing on the page explaining why. This modal makes
 * the second half of the decision explicit at the moment the first half is made.
 *
 * Everyone who CAN be given access is ticked by default, because that is what
 * somebody pressing "Approve Contractor" almost always means. Unticking is how
 * you say otherwise.
 *
 * Only shown when the property is on MANUAL contractor approval. Under automatic
 * approval a check-in has already produced an active login, so there is nothing
 * to ask; under reject, access is refused outright.
 */
const ApproveContractorModal = ({ open, contractor, onCancel, onApproved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [selected, setSelected] = useState([]);

  const selectable = useMemo(() => workers.filter((w) => w.selectable), [workers]);

  useEffect(() => {
    if (!open || !contractor?.id) return;
    setLoading(true);
    contractorService
      .accessCandidates(contractor.id)
      .then((res) => {
        if (res?.code === "0000") {
          const list = res.workers || [];
          setWorkers(list);
          // Default is everyone who can be given access.
          setSelected(list.filter((w) => w.selectable).map((w) => w.id));
        } else {
          message.error(res?.message || "Could not load personnel");
          setWorkers([]);
          setSelected([]);
        }
      })
      .catch(() => {
        message.error("Could not load personnel");
        setWorkers([]);
        setSelected([]);
      })
      .finally(() => setLoading(false));
  }, [open, contractor]);

  const allSelected = selectable.length > 0 && selected.length === selectable.length;
  const someSelected = selected.length > 0 && !allSelected;

  const toggleAll = (e) =>
    setSelected(e.target.checked ? selectable.map((w) => w.id) : []);

  const submit = () => {
    setSaving(true);
    contractorService
      .approveWithAccess(contractor.id, selected)
      .then((res) => {
        if (res?.code !== "0000") {
          message.error(res?.message || "Failed to approve contractor");
          return;
        }
        // Report what actually happened per person rather than a bare
        // "approved": some rows are skipped (no email) and some were already
        // active, and silently doing less than was ticked is how trust in a bulk
        // action gets lost.
        const results = Object.values(res.results || {});
        const created = results.filter((r) => r.status === "account_created").length;
        const granted = results.filter((r) => r.status === "access_granted").length;
        const skipped = results.filter((r) => r.status === "no_email").length;
        const failed = results.filter((r) => r.status === "error").length;

        const parts = ["Contractor approved"];
        if (created) parts.push(`${created} account${created > 1 ? "s" : ""} created`);
        if (granted) parts.push(`${granted} given access`);
        if (skipped) parts.push(`${skipped} skipped (no email)`);
        message.success(parts.join(" — "));
        if (failed) {
          message.warning(`${failed} could not be given access. Please try those again.`);
        }

        onApproved(res.contractor);
      })
      .catch(() => message.error("Failed to approve contractor"))
      .finally(() => setSaving(false));
  };

  const columns = [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          disabled={selectable.length === 0}
          onChange={toggleAll}
        >
          Person
        </Checkbox>
      ),
      key: "name",
      render: (_t, row) => (
        <Checkbox
          checked={selected.includes(row.id)}
          disabled={!row.selectable}
          onChange={(e) =>
            setSelected((prev) =>
              e.target.checked ? [...prev, row.id] : prev.filter((id) => id !== row.id)
            )
          }
        >
          <span className="reg-main">{row.full_name}</span>
          {row.role && <span className="reg-sub"> · {row.role}</span>}
        </Checkbox>
      ),
    },
    {
      title: "Email",
      key: "email",
      render: (_t, row) =>
        row.email || <span className="reg-muted">No email address</span>,
    },
    {
      title: "Site Access",
      key: "site_access",
      render: (_t, row) => {
        const meta = SITE_ACCESS_META[row.site_access] || SITE_ACCESS_META.pending;
        return (
          <Tooltip title={meta.hint}>
            <span className={`status-pill-sm ${meta.className}`}>{meta.label}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "",
      key: "reason",
      render: (_t, row) =>
        row.reason ? <span className="reg-muted">{row.reason}</span> : null,
    },
  ];

  return (
    <Modal
      title="Approve contractor"
      open={open}
      onCancel={onCancel}
      onOk={submit}
      okText={
        selected.length
          ? `Approve and give ${selected.length} ${selected.length === 1 ? "person" : "people"} access`
          : "Approve without granting access"
      }
      confirmLoading={saving}
      width={760}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Approving the contractor does not, by itself, let anyone sign in."
        description={
          <>
            Approving records that <strong>{contractor?.business_name}</strong> is compliant. Tick
            the people below who should also be able to <strong>sign in to this property</strong>.
            Anyone ticked gets an account if they do not have one, and is emailed a temporary
            password. You can change this later under Property Settings → User Access Control.
          </>
        }
      />

      {loading ? (
        <div style={{ textAlign: "center", padding: 32 }}>
          <Spin />
        </div>
      ) : (
        <Table
          size="small"
          rowKey="id"
          columns={columns}
          dataSource={workers}
          pagination={false}
          locale={{
            emptyText:
              "No personnel are registered for this contractor yet. You can still approve the business.",
          }}
        />
      )}
    </Modal>
  );
};

export default ApproveContractorModal;
